import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, filter, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Notification } from '../entites/notification';
import { SihcHubService, SihcEvents } from '../services/sihc-hub.service';
import { SwUpdate } from '@angular/service-worker';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private http = inject(HttpClient);
    private messageService = inject(MessageService);
    private notificationHub = inject(SihcHubService);
    private swUpdate = inject(SwUpdate, { optional: true });

    private notificationsSubject = new BehaviorSubject<Notification[]>([]);
    public notifications$ = this.notificationsSubject.asObservable();

    private unreadCountSubject = new BehaviorSubject<number>(0);
    public unreadCount$ = this.unreadCountSubject.asObservable();

    private apiUrl = `${environment.apiBaseUrl}notification`;

    constructor() {
        this.notificationHub.connection.on(SihcEvents.notificationReceived, (notification: Notification) => {
            this.handleNewNotification(notification);
        });
        void this.notificationHub.subscribe('notifications');
        this.subscribeToAppUpdates();
    }

    /**
     * Subscribes to service worker version updates and notifies the user when a new app version is available.
     */
    private subscribeToAppUpdates(): void {
        if (!this.swUpdate?.isEnabled) {
            return;
        }
        this.swUpdate.versionUpdates
            .pipe(filter(event => event.type === 'VERSION_READY'))
            .subscribe(() => {
                this.showAppUpdateNotification();
            });
        this.swUpdate.checkForUpdate().catch(() => {
            // Ignore errors (e.g. when offline or no SW)
        });
    }

    public connect() {
        void this.notificationHub.subscribe('notifications');
        this.obtenirNotifications().subscribe();
    }

    public disconnect() {
        this.notificationHub.unsubscribe('notifications');
    }

    /**
     * Checks if the SignalR connection is up and running.
     * Call this before generating a rapport to ensure live notifications will be received.
     */
    public isSignalRConnected(): boolean {
        return this.notificationHub?.isConnected() ?? false;
    }

    /**
     * Ensures SignalR is ready before generating a rapport. Attempts reconnection if down.
     * Shows an error to the user if the connection cannot be restored.
     * @returns true if connected, false otherwise
     */
    public async ensureSignalRReadyBeforeRapport(): Promise<boolean> {
        if (this.isSignalRConnected()) {
            return true;
        }
        await this.notificationHub.subscribe('notifications');
        await new Promise(resolve => setTimeout(resolve, 2000));
        if (this.isSignalRConnected()) {
            return true;
        }
        this.messageService.add({
            key: 'notification',
            severity: 'error',
            summary: 'Connexion aux notifications interrompue',
            detail: 'La génération de rapports ne fonctionnera pas correctement. Veuillez rafraîchir la page pour rétablir la connexion.',
            life: 10000
        });
        return false;
    }

    public obtenirNotifications(): Observable<Notification[]> {
        return this.http.get<Notification[]>(this.apiUrl).pipe(
            tap(notifications => {
                this.notificationsSubject.next(notifications);
                this.updateUnreadCount(notifications);
            })
        );
    }

    public marquerCommeLu(id: number): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/marquer-lu/${id}`, {}).pipe(
            tap(() => {
                const current = this.notificationsSubject.value;
                const updated = current.map(n => 
                    n.notificationDestinataireId === id ? { ...n, indLu: true } : n
                );
                this.notificationsSubject.next(updated);
                this.updateUnreadCount(updated);
            })
        );
    }

    public marquerToutesCommeLu(): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/marquer-toutes-lu`, {}).pipe(
            tap(() => {
                const current = this.notificationsSubject.value;
                const updated = current.map(n => ({ ...n, indLu: true }));
                this.notificationsSubject.next(updated);
                this.unreadCountSubject.next(0);
            })
        );
    }

    public supprimer(id: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
            tap(() => {
                const updated = this.notificationsSubject.value.filter(n => n.notificationDestinataireId !== id);
                this.notificationsSubject.next(updated);
                this.updateUnreadCount(updated);
            })
        );
    }

    public supprimerToutes(): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/toutes`).pipe(
            tap(() => {
                this.notificationsSubject.next([]);
                this.unreadCountSubject.next(0);
            })
        );
    }

    private handleNewNotification(notification: Notification) {
        const current = this.notificationsSubject.value;
        this.notificationsSubject.next([notification, ...current]);
        this.updateUnreadCount([notification, ...current]);

        const n = notification as Notification & { Titre?: string; Description?: string };
        if (this.estNotificationSyncCtrl(n.titre, n.Titre, n.description, n.Description)) {
            return;
        }

        this.showNotification({
            severity: 'info',
            summary: notification.titre,
            detail: notification.description,
            donneesPersonnalisees: notification.donneesPersonnalisees
        });
    }

    private updateUnreadCount(notifications: Notification[]) {
        const count = notifications.filter(n => !n.indLu).length;
        this.unreadCountSubject.next(count);
    }

    private estNotificationSyncCtrl(...parties: Array<string | null | undefined>): boolean {
        const texte = parties
            .map((p) => p ?? '')
            .join(' ')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        return (
            texte.includes('synchronisation ctrl') ||
            texte.includes('synchronise avec ctrl') ||
            texte.includes('synchronisee avec ctrl')
        );
    }

    public showNotification(notification: { severity: string, summary: string, detail: string, donneesPersonnalisees?: any }) {
        if (this.estNotificationSyncCtrl(notification.summary, notification.detail)) {
            return;
        }
        this.messageService.add({
            key: 'notification',
            severity: notification.severity,
            summary: notification.summary,
            detail: notification.detail,
            data: notification.donneesPersonnalisees,
            life: 5000
        });
    }

    public showUploadProgress(fileName: string, initialProgress: number = 0, onRetry?: () => void) {
        const progressSubject = new BehaviorSubject<{progress: number, success: boolean | null}>({progress: initialProgress, success: null});

        this.messageService.add({
            key: 'notification',
            severity: 'info',
            summary: 'Téléversement en cours',
            detail: {
                type: 'upload',
                upload: {
                    name: fileName,
                    progress: progressSubject,
                    onRetry: onRetry
                }
            } as any,
            sticky: true
        });

        return progressSubject;
    }

    /**
     * Shows a notification when a new app version is available, with an option to refresh.
     */
    private showAppUpdateNotification(): void {
        const onReload = () => {
            this.swUpdate?.activateUpdate().then(() => window.location.reload());
        };
        this.messageService.add({ 
            key: 'notification',
            severity: 'info',
            summary: 'Nouvelle version disponible',
            detail: {
                type: 'app-update',
                onReload
            } as any,
            sticky: true
        });
    }
}
